// Columna central del explorer: las 9 columnas del mock, pintadas con el `DataTable` compartido.
import { cn } from '@/lib/cn';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import type { CreditTone } from '@/domains/credit/creditFile/logic';
import {
    AGENDA_COLUMN_LABELS,
    AGENDA_EMPTY_MESSAGE,
    PRIORITY_TONE,
    formatAgendaMoney,
    formatShortDay,
} from '../format';
import {
    activePromiseOf,
    brokenPromiseOf,
    computePriority,
    daysBetween,
    lastContactOf,
} from '../logic';
import type { AgendaSort, AgendaSortKey, CollectorAgendaRow, ContactChannel } from '../types';

/** Relleno + borde del chip, como `.chip-*` del mock. */
const CHIP_TONE: Record<CreditTone, string> = {
    success: 'border-success-soft-border bg-success-soft text-success',
    danger: 'border-danger-soft-border bg-danger-soft text-danger',
    warning: 'border-warning-soft-border bg-warning-soft text-warning',
    info: 'border-info-soft-border bg-info-soft text-info',
    neutral: 'border-border bg-muted text-muted-foreground',
};

/** El icono de canal de la celda «Últ. contacto», como el mock. */
const CHANNEL_ICON: Record<ContactChannel, string> = {
    call: '📞',
    sms: '💬',
    chat: '💬',
    whatsapp: '💬',
    voice_blaster: '🔊',
};

const NO_VALUE = '—';

function Chip({ tone, title, children }: { tone: CreditTone; title?: string; children: string }) {
    return (
        <span
            title={title}
            className={cn(
                'inline-flex items-center whitespace-nowrap rounded-full border px-[5px] py-px text-[9px] font-medium',
                CHIP_TONE[tone],
            )}
        >
            {children}
        </span>
    );
}

function Missing() {
    return <span className="text-[10.5px] text-muted-foreground">{NO_VALUE}</span>;
}

/**
 * Las 8 columnas fijas suman 664px y Cliente pide el resto. Por debajo de esto la tabla scrollea
 * dentro de su panel: el mock las comprime, pero comprimidas «CR-0003» y el nombre del cliente
 * salen cortados y la tabla deja de servir para lo que existe.
 */
const TABLE_MIN_WIDTH = '800px';

function buildColumns(today: string): DataTableColumn<CollectorAgendaRow, AgendaSortKey>[] {
    return [
        {
            key: 'priority',
            label: AGENDA_COLUMN_LABELS[0],
            sortKey: 'priority',
            width: '112px',
            cellOverflow: 'nowrap',
            cell: (row) => {
                const priority = computePriority(row, today);
                if (priority === null) return <Missing />;
                return (
                    <span
                        className={cn(
                            'inline-flex items-center gap-[3px] whitespace-nowrap rounded border px-1.5 py-px text-[9.5px] font-bold',
                            CHIP_TONE[PRIORITY_TONE[priority.rank]],
                        )}
                    >
                        {priority.label}
                    </span>
                );
            },
        },
        {
            key: 'creditId',
            label: AGENDA_COLUMN_LABELS[1],
            sortKey: 'creditId',
            width: '76px',
            cell: (row) => (
                // eslint-disable-next-line no-restricted-syntax -- columna «CR»: el folio ES lo que el mock pinta
                <span className="font-mono text-[11px]">{row.creditId}</span>
            ),
        },
        {
            key: 'customer',
            label: AGENDA_COLUMN_LABELS[2],
            sortKey: 'customer',
            cell: (row) => <span className="font-medium">{row.customerName}</span>,
        },
        {
            key: 'overdueInstallments',
            label: AGENDA_COLUMN_LABELS[3],
            sortKey: 'overdueInstallments',
            numeric: true,
            width: '60px',
            cell: (row) => <span className="text-[11px]">{row.overdueInstallments}</span>,
        },
        {
            key: 'overdueAmount',
            label: AGENDA_COLUMN_LABELS[4],
            sortKey: 'overdueAmount',
            numeric: true,
            width: '96px',
            cell: (row) => formatAgendaMoney(row.overdueAmountCents),
        },
        {
            key: 'overdueDays',
            label: AGENDA_COLUMN_LABELS[5],
            sortKey: 'overdueDays',
            width: '52px',
            cellOverflow: 'nowrap',
            note: 'Días de mora del crédito.',
            cell: (row) => {
                if (row.overdueDays <= 0) return <Missing />;
                const tone: CreditTone =
                    row.overdueDays > 30 ? 'danger' : row.overdueDays > 7 ? 'warning' : 'info';
                return <Chip tone={tone}>{`${String(row.overdueDays)}d`}</Chip>;
            },
        },
        {
            key: 'lastContact',
            label: AGENDA_COLUMN_LABELS[6],
            sortKey: 'lastContact',
            width: '110px',
            cellOverflow: 'nowrap',
            cell: (row) => {
                const last = lastContactOf(row);
                if (last === null) {
                    return (
                        <span className="text-[10.5px] text-muted-foreground">Sin contacto</span>
                    );
                }
                const days = daysBetween(last.at, today);
                const label = days === 0 ? 'hoy' : days === 1 ? 'ayer' : `${String(days)}d`;
                return (
                    <Chip tone={days >= 3 ? 'warning' : 'neutral'}>
                        {`${CHANNEL_ICON[last.channel]} ${label}`}
                    </Chip>
                );
            },
        },
        {
            key: 'attempts',
            label: AGENDA_COLUMN_LABELS[7],
            width: '66px',
            cellOverflow: 'nowrap',
            note: 'Intentos sin respuesta en 48 h.',
            cell: (row) => {
                if (row.recentAttempts <= 0) return <Missing />;
                const tone: CreditTone =
                    row.recentAttempts === 1
                        ? 'info'
                        : row.recentAttempts === 2
                          ? 'warning'
                          : 'danger';
                return (
                    <Chip tone={tone} title="Intentos sin respuesta en 48 h">
                        {`${String(Math.min(row.recentAttempts, 3))}/3`}
                    </Chip>
                );
            },
        },
        {
            key: 'promise',
            label: AGENDA_COLUMN_LABELS[8],
            sortKey: 'promise',
            width: '92px',
            cellOverflow: 'nowrap',
            cell: (row) => {
                const active = activePromiseOf(row);
                if (active !== null) {
                    return (
                        <Chip tone={active.day === today ? 'info' : 'neutral'}>
                            {formatShortDay(active.day)}
                        </Chip>
                    );
                }
                if (brokenPromiseOf(row) !== null) return <Chip tone="danger">Incumpl.</Chip>;
                return <Missing />;
            },
        },
    ];
}

/** Un clic sobre el mismo encabezado invierte el sentido; sobre otro arranca ascendente. */
export function nextAgendaSort(current: AgendaSort, key: AgendaSortKey): AgendaSort {
    if (current.key !== key) return { key, direction: 'asc' };
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

export interface AgendaTableProps {
    rows: readonly CollectorAgendaRow[];
    /** El día contra el que se calculan prioridad, antigüedad del contacto y compromiso de hoy. */
    today: string;
    sort: AgendaSort;
    onSortChange: (next: AgendaSort) => void;
    selectedCreditId: string | null;
    onSelect: (creditId: string) => void;
    className?: string;
}

export function AgendaTable({
    rows,
    today,
    sort,
    onSortChange,
    selectedCreditId,
    onSelect,
    className,
}: AgendaTableProps) {
    return (
        <DataTable
            columns={buildColumns(today)}
            rows={rows}
            rowKey={(row) => row.creditId}
            minWidth={TABLE_MIN_WIDTH}
            caption="Créditos en la agenda del cobrador, con su prioridad, mora, último contacto y compromiso."
            sort={sort}
            onSortToggle={(key) => {
                onSortChange(nextAgendaSort(sort, key));
            }}
            isRowSelected={(row) => row.creditId === selectedCreditId}
            onRowClick={(row) => {
                onSelect(row.creditId);
            }}
            empty={AGENDA_EMPTY_MESSAGE}
            className={className}
        />
    );
}
